import { createReducer, combineReducersWithRoot } from '@common/redux-utils';
import { AutoAuthoViewModel } from '@services/AutoAuthoService';
import { OptionsViewModel } from '@models/common.models';

import {
  AuthorizeFailureAction,
  AuthorizeStartAction,
  AuthorizeSuccessAction,
  AutoAuthoActionType,
  GetSwoTripStatusesFailureAction,
  GetSwoTripStatusesStartAction,
  GetSwoTripStatusesSuccessAction,
  GetViewModelFailureAction,
  GetViewModelStartAction,
  GetViewModelSuccessAction,
  GotoPageAction,
  UpdateViewModelAction
} from './auto-autho.actions';
import { AutoAuthoPage, AutoAuthoResult } from './_components/auto-autho.component';

import {
  autoAuthoStatusUpdateInitialState,
  AutoAuthoStatusUpdateReducer,
  AutoAuthoStatusUpdateState
} from './_components/status-update/status-update.reducer';

// Defining the state of the page.
// Should be one per page.
// All properties should be readonly.
export interface AutoAuthoState {
  authorizationResult?: AutoAuthoResult;
  hasConfirmationFailed: boolean;
  page: AutoAuthoPage;
  phoneNumber?: string;
  statusUpdate: AutoAuthoStatusUpdateState;
  swoId?: number;
  swoTripStatusMap?: object;
  viewModel?: AutoAuthoViewModel;
}

// This is the initial state of the page
const initialState: AutoAuthoState = {
  hasConfirmationFailed: false,
  page: AutoAuthoPage.Validation,
  statusUpdate: autoAuthoStatusUpdateInitialState
};

function handleAuthorizeFailure(state: AutoAuthoState, action: AuthorizeFailureAction): AutoAuthoState {
  return {
    ...state,
    hasConfirmationFailed: true
  };
}

function handleAuthorizeStart(state: AutoAuthoState, action: AuthorizeStartAction): AutoAuthoState {
  return {
    ...state
  };
}

function handleAuthorizeSuccess(state: AutoAuthoState, action: AuthorizeSuccessAction): AutoAuthoState {
  return {
    ...state,
    authorizationResult: action.result,
    hasConfirmationFailed: false,
    page: action.result.isConfirmed ? AutoAuthoPage.AuthorizationConfirmed : AutoAuthoPage.AuthorizationDenied
  };
}

function handleGetSwoTripStatusesFailure(
  state: AutoAuthoState,
  action: GetSwoTripStatusesFailureAction
): AutoAuthoState {
  return {
    ...state
  };
}

function handleGetSwoTripStatusesSuccess(
  state: AutoAuthoState,
  action: GetSwoTripStatusesSuccessAction
): AutoAuthoState {
  const swoTripStatusMap: object = {};
  for (let i: number = 0; i < action.statuses.length; ++i) {
    let s: OptionsViewModel = action.statuses[i];
    swoTripStatusMap[s.id] = s.value;
  }
  return {
    ...state,
    swoTripStatusMap: swoTripStatusMap
  };
}

function handleGetSwoTripStatusesStart(state: AutoAuthoState, action: GetSwoTripStatusesStartAction): AutoAuthoState {
  return {
    ...state
  };
}

function handleGetViewModelFailure(state: AutoAuthoState, action: GetViewModelFailureAction): AutoAuthoState {
  return {
    ...state,
    viewModel: undefined
  };
}

function handleGetViewModelStart(state: AutoAuthoState, action: GetViewModelStartAction): AutoAuthoState {
  return {
    ...state,
    viewModel: undefined
  };
}

function handleGetViewModelSuccess(state: AutoAuthoState, action: GetViewModelSuccessAction): AutoAuthoState {
  if (action.rememberPhoneNumber) {
    localStorage.setItem('autoAuthoPhoneNumber', action.phoneNumber);
  } else {
    localStorage.setItem('autoAuthoPhoneNumber', '');
  }
  return {
    ...state,
    page: AutoAuthoPage.Landing,
    phoneNumber: action.phoneNumber,
    swoId: action.swoId,
    viewModel: action.viewModel
  };
}

function handleGotoPage(state: AutoAuthoState, action: GotoPageAction): AutoAuthoState {
  return {
    ...state,
    page: action.page
  };
}

function handleUpdateViewModel(state: AutoAuthoState, action: UpdateViewModelAction): AutoAuthoState {
  return {
    ...state,
    viewModel: action.viewModel
  };
}

const reducer = createReducer(initialState, {
  [AutoAuthoActionType.AuthorizeFailure]: handleAuthorizeFailure,
  [AutoAuthoActionType.AuthorizeStart]: handleAuthorizeStart,
  [AutoAuthoActionType.AuthorizeSuccess]: handleAuthorizeSuccess,
  [AutoAuthoActionType.GetSwoTripStatusesFailure]: handleGetSwoTripStatusesFailure,
  [AutoAuthoActionType.GetSwoTripStatusesStart]: handleGetSwoTripStatusesStart,
  [AutoAuthoActionType.GetSwoTripStatusesSuccess]: handleGetSwoTripStatusesSuccess,
  [AutoAuthoActionType.GetViewModelFailure]: handleGetViewModelFailure,
  [AutoAuthoActionType.GetViewModelStart]: handleGetViewModelStart,
  [AutoAuthoActionType.GetViewModelSuccess]: handleGetViewModelSuccess,
  [AutoAuthoActionType.GotoPage]: handleGotoPage,
  [AutoAuthoActionType.UpdateViewModel]: handleUpdateViewModel
});

// This creates the reducer as redux wants it w/ a helper function
// There should an entry for each action type
export const AutoAuthoReducer = combineReducersWithRoot(reducer, {
  statusUpdate: AutoAuthoStatusUpdateReducer
});



// WEBPACK FOOTER //
// ./src/auto-autho/auto-autho.reducer.ts